import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Car, Plus, LayoutGrid, LogOut, User as UserIcon, Menu, Bell, CreditCard, MessageCircle, ShieldAlert, Heart } from "lucide-react";
import { useState, useEffect } from "react";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); setMsgUnread(0); return; }
    api.get("/alerts/unread_count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    api.get("/conversations/unread_count").then(({ data }) => setMsgUnread(data.unread || 0)).catch(() => {});
  }, [user]);

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/painel" });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Você ainda não tem assinatura ativa."); }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-sm bg-obsidian flex items-center justify-center">
            <Car className="w-5 h-5 text-blaze" strokeWidth={2} />
          </span>
          <span className="font-heading font-800 text-xl tracking-tight">Pic<span className="text-blaze">Car</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/buscar" className="text-sm font-600 hover:text-blaze transition-colors" data-testid="nav-buscar">Buscar Carros</Link>
          <Link to="/como-funciona" className="text-sm font-600 hover:text-blaze transition-colors" data-testid="nav-como">Como Funciona</Link>
          <Link to="/materiais" className="text-sm font-600 hover:text-blaze transition-colors" data-testid="nav-materiais">Kit de Divulgação com QR</Link>
          <Link to="/precos" className="text-sm font-600 hover:text-blaze transition-colors" data-testid="nav-precos">Preços</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button variant="ghost" className="font-600" onClick={() => navigate("/anunciar")} data-testid="nav-anunciar-btn">
            <Plus className="w-4 h-4 mr-1" /> Anunciar Grátis
          </Button>
          {user ? (
            <>
              <button onClick={() => navigate("/mensagens")} className="relative w-10 h-10 rounded-sm hover:bg-secondary flex items-center justify-center transition-colors" data-testid="nav-mensagens-btn" aria-label="Mensagens">
                <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                {msgUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-blaze text-white text-[10px] font-700 flex items-center justify-center" data-testid="nav-mensagens-badge">{msgUnread}</span>
                )}
              </button>
              <button onClick={() => navigate("/favoritos")} className="relative w-10 h-10 rounded-sm hover:bg-secondary flex items-center justify-center transition-colors" data-testid="nav-favoritos-btn" aria-label="Favoritos">
                <Heart className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button onClick={() => navigate("/alertas")} className="relative w-10 h-10 rounded-sm hover:bg-secondary flex items-center justify-center transition-colors" data-testid="nav-alertas-btn" aria-label="Alertas">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-blaze text-white text-[10px] font-700 flex items-center justify-center" data-testid="nav-alertas-badge">{unread}</span>
                )}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="font-600 border-obsidian/15" data-testid="nav-user-menu">
                    <UserIcon className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0] || "Conta"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => navigate("/painel")} data-testid="menu-painel"><LayoutGrid className="w-4 h-4 mr-2" /> Meus Anúncios</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mensagens")} data-testid="menu-mensagens"><MessageCircle className="w-4 h-4 mr-2" /> Mensagens</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favoritos")} data-testid="menu-favoritos"><Heart className="w-4 h-4 mr-2" /> Favoritos</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/alertas")} data-testid="menu-alertas"><Bell className="w-4 h-4 mr-2" /> Meus Alertas</DropdownMenuItem>
                  <DropdownMenuItem onClick={openPortal} data-testid="menu-portal"><CreditCard className="w-4 h-4 mr-2" /> Gerenciar assinatura</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin/denuncias")} data-testid="menu-admin-denuncias"><ShieldAlert className="w-4 h-4 mr-2" /> Denúncias</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="text-blaze" data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> Sair</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button className="bg-obsidian hover:bg-blaze text-white font-600 rounded-sm" onClick={() => navigate("/entrar")} data-testid="nav-entrar-btn">Entrar</Button>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle"><Menu className="w-6 h-6" /></button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white px-5 py-4 flex flex-col gap-3" data-testid="nav-mobile-menu">
          <Link to="/buscar" onClick={() => setOpen(false)} className="font-600">Buscar Carros</Link>
          <Link to="/como-funciona" onClick={() => setOpen(false)} className="font-600">Como Funciona</Link>
          <Link to="/materiais" onClick={() => setOpen(false)} className="font-600">Kit de Divulgação com QR</Link>
          <Link to="/precos" onClick={() => setOpen(false)} className="font-600">Preços</Link>
          <Link to="/anunciar" onClick={() => setOpen(false)} className="font-600">Anunciar Grátis</Link>
          {user ? (
            <>
              <Link to="/painel" onClick={() => setOpen(false)} className="font-600">Meus Anúncios</Link>
              <Link to="/alertas" onClick={() => setOpen(false)} className="font-600">Meus Alertas {unread > 0 && <span className="text-blaze">({unread})</span>}</Link>
              <button onClick={() => { logout(); setOpen(false); }} className="text-left font-600 text-blaze">Sair</button>
            </>
          ) : (
            <Link to="/entrar" onClick={() => setOpen(false)} className="font-600 text-blaze">Entrar</Link>
          )}
        </div>
      )}
    </header>
  );
}
